import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  RiskProfile, 
  RiskProfileInput, 
  getRiskProfile, 
  upsertRiskProfile,
  calculateRiskAmount,
  calculateMaxDailyLoss,
  buildRiskProfileContext
} from '@/services/riskProfileService';

interface UseRiskProfileReturn {
  profile: RiskProfile | null;
  loading: boolean;
  error: string | null;
  riskAmount: number;
  maxDailyLoss: number;
  contextString: string;
  updateProfile: (data: RiskProfileInput) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const useRiskProfile = (): UseRiskProfileReturn => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<RiskProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getRiskProfile(user.id);
      setProfile(data);
    } catch (err) {
      console.error('Failed to fetch risk profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load risk profile');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(async (data: RiskProfileInput) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      const updated = await upsertRiskProfile(user.id, data);
      setProfile(updated);
    } catch (err) {
      console.error('Failed to update risk profile:', err);
      const message = err instanceof Error ? err.message : 'Failed to update risk profile';
      setError(message);
      throw err;
    }
  }, [user?.id]);

  return {
    profile,
    loading,
    error,
    riskAmount: calculateRiskAmount(profile),
    maxDailyLoss: calculateMaxDailyLoss(profile),
    contextString: buildRiskProfileContext(profile),
    updateProfile,
    refreshProfile: fetchProfile
  };
};

export default useRiskProfile;
